/* eslint-disable react-refresh/only-export-components -- shared module of
   components + formatters, consumed by both Channel Console modules; splitting
   the formatters into a second file would fight D11's stated intent to keep
   this one sibling file. */
/**
 * Channel Console — shared UI primitives (A7Y-3778, D11).
 *
 * `Card`, `Notice` and the formatters extracted verbatim out of
 * `WebsiteModule.tsx` (A7Y-3776) so the Product module (A7Y-3778) can consume
 * them without copying — the DRY violation the ticket forbids. No behavior or
 * visual change from the Website module's originals.
 *
 * Kept in the page directory rather than promoted to `@a7y/ui`: these are
 * admin-dashboard-idiom components consumed by exactly the two Channel
 * Console modules, the same reasoning `types.ts` states for keeping the
 * payload shapes local.
 */
import { Sparkline } from '@/pages/audit-spend/Sparkline';

/** Full local date + time, or an em dash for a missing/invalid timestamp. */
export function formatLocal(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function Card({
  title,
  primary,
  secondary,
  sparkline,
}: {
  title: string;
  primary: string;
  secondary?: string;
  sparkline?: number[];
}) {
  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-xs uppercase tracking-wide text-zinc-500">{title}</h3>
          <div className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-50">
            {primary}
          </div>
          {secondary && <div className="mt-0.5 text-xs text-zinc-500">{secondary}</div>}
        </div>
        {sparkline && sparkline.length > 0 && (
          <div className="text-blue-500 dark:text-blue-400 w-24 h-8">
            <Sparkline values={sparkline} width={96} height={32} />
          </div>
        )}
      </div>
    </div>
  );
}

export function Notice({
  tone,
  title,
  children,
}: {
  tone: 'warn' | 'info';
  title: string;
  children: React.ReactNode;
}) {
  const palette =
    tone === 'warn'
      ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40'
      : 'border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900';
  return (
    <div className={`rounded-lg border p-4 ${palette}`}>
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
      <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{children}</div>
    </div>
  );
}

export function formatInt(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

export function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}
